import { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, deleteDoc, doc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useToast } from '@/context/ToastContext';

export interface SavedLocation {
    id: string;
    name: string;
    x: number;
    y: number;
    z: number;
    timestamp: any;
}

export const useLocations = (userId: string | undefined) => {
    const [locations, setLocations] = useState<SavedLocation[]>([]);
    const [loading, setLoading] = useState(true);
    const { showToast } = useToast();

    useEffect(() => {
        if (!userId) {
            setLocations([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        const locationsRef = collection(db, 'users', userId, 'locations');
        const q = query(locationsRef, orderBy('timestamp', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const locs: SavedLocation[] = [];
            snapshot.forEach((doc) => {
                locs.push({ id: doc.id, ...doc.data() } as SavedLocation);
            });
            setLocations(locs);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching locations:", error);
            showToast("Failed to load locations", "error");
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userId, showToast]);

    const addLocation = async (name: string, position: { x: number, y: number, z: number }) => {
        if (!userId) return;
        try {
            await addDoc(collection(db, 'users', userId, 'locations'), {
                name,
                x: position.x,
                y: position.y,
                z: position.z,
                timestamp: serverTimestamp()
            });
            showToast(`Location "${name}" saved!`, "success");
        } catch (error) {
            console.error("Error saving location:", error);
            showToast("Failed to save location", "error");
        }
    };

    const deleteLocation = async (id: string) => {
        if (!userId) return;
        try {
            await deleteDoc(doc(db, 'users', userId, 'locations', id));
            showToast("Location deleted", "info");
        } catch (error) {
            console.error("Error deleting location:", error);
            showToast("Failed to delete location", "error");
        }
    };

    return { locations, loading, addLocation, deleteLocation };
};
